// Operator sign-in (SM-752): username + password against the mock's own
// operator store (POST /mock-admin/auth/login). The answer sets the
// HttpOnly session cookie; the console keeps only the identity in the
// in-memory query cache.
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { APIError } from "@/lib/api";
import { fetchMeOrNull, keys, login } from "@/lib/queries";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";

interface LoginSearch {
  redirect?: string;
}

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  beforeLoad: async ({ context, search }) => {
    const me = await context.queryClient.ensureQueryData({
      queryKey: keys.me(),
      queryFn: fetchMeOrNull,
      staleTime: 5_000,
    });
    if (me) throw redirect({ to: safeRedirect(search.redirect) });
  },
  component: LoginPage,
});

// Only same-app paths: never bounce to another origin after sign-in.
function safeRedirect(r: string | undefined): string {
  if (!r || !r.startsWith("/") || r.startsWith("//")) return "/users";
  return r;
}

function humanError(e: unknown): string {
  if (e instanceof APIError) {
    if (e.status === 0) return e.message;
    if (e.status >= 500) return "The provider mock is unavailable right now. Try again shortly.";
    return e.message;
  }
  return e instanceof Error && e.message ? e.message : "Sign-in failed.";
}

function LoginPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const qc = useQueryClient();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => login(username.trim(), password),
    onSuccess: (me) => {
      setErr(null);
      setPassword("");
      qc.setQueryData(keys.me(), me);
      navigate({ to: safeRedirect(search.redirect) });
    },
    onError: (e) => setErr(humanError(e)),
  });

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-12 sm:px-6">
      <div className="w-full max-w-md">
        <div className="mb-6">
          <div className="text-2xs uppercase tracking-[0.18em] text-t-3">Smart Market · SM-621</div>
          <div className="mt-1 text-lg font-semibold text-t-1">Provider mock console</div>
        </div>
        <div className="modal">
          <div className="modal-glow" />
          <div className="modal-body">
            <div className="auth-eyebrow">Sandbox operator</div>
            <h2 className="auth-headline">Sign in</h2>
            <p className="auth-sub">
              This console drives the provider <b>mock</b> only — sandbox wallets, launch codes and fault rules. It holds
              no production credential and moves no real funds.
            </p>
            {err && (
              <div className="banner danger" role="alert">
                <span aria-hidden>⚠</span>
                <span>{err}</span>
              </div>
            )}
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                mut.mutate();
              }}
            >
              <Input
                label="Username"
                type="text"
                name="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                autoFocus
                required
              />
              <Input
                label="Password"
                type="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
              <Button type="submit" loading={mut.isPending} className="btn-block btn-lg mt-1">
                {mut.isPending ? "Signing in…" : "Sign in"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
